/**
 * DigitalPassPage — PUBLIC (no auth). Route: /pass/:guestCode
 *
 * Shows the guest's name, event details, and a QR code encoding the guestCode.
 * The QR is what gate staff scan at the venue. Mobile-first card layout so it
 * works as a screenshot on any phone (no app install required).
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { getGuestPass, type GuestPass } from '@/src/modules/guests/api';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; pass: GuestPass }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(d);
}

export function DigitalPassPage() {
  const { guestCode = '' } = useParams<{ guestCode: string }>();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ kind: 'loading' });
    getGuestPass(guestCode)
      .then((pass) => active && setState({ kind: 'ready', pass }))
      .catch((err: Error) => {
        if (!active) return;
        setState(
          err.message === 'NOT_FOUND'
            ? { kind: 'not-found' }
            : { kind: 'error', message: err.message },
        );
      });
    return () => {
      active = false;
    };
  }, [guestCode]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {state.kind === 'loading' && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin" />
          </div>
        )}

        {state.kind === 'not-found' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="text-5xl mb-3">🔍</div>
            <h1 className="text-xl font-bold text-slate-800">Pass not found</h1>
            <p className="text-slate-500 mt-2 text-sm">
              This invitation link is invalid or has been removed. Please check
              the link from your invitation email.
            </p>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="text-5xl mb-3">⚠️</div>
            <h1 className="text-xl font-bold text-slate-800">Something went wrong</h1>
            <p className="text-slate-500 mt-2 text-sm">{state.message}</p>
          </div>
        )}

        {state.kind === 'ready' && <PassCard pass={state.pass} />}
      </div>
    </div>
  );
}

function PassCard({ pass }: { pass: GuestPass }) {
  const date = formatDate(pass.eventDate);
  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="bg-indigo-600 text-white px-6 py-5 text-center">
        <p className="uppercase tracking-widest text-xs text-indigo-200">
          Event Pass
        </p>
        <h1 className="text-2xl font-bold mt-1">{pass.guestName}</h1>
      </div>

      <div className="px-6 py-5 text-center border-b border-slate-100">
        <h2 className="text-lg font-semibold text-slate-800">{pass.eventName}</h2>
        {date && <p className="text-slate-500 text-sm mt-1">📅 {date}</p>}
        {pass.eventVenue && (
          <p className="text-slate-500 text-sm mt-1">📍 {pass.eventVenue}</p>
        )}
      </div>

      <div className="px-6 py-6 flex flex-col items-center">
        <div className="bg-white p-3 rounded-lg border border-slate-200">
          <QRCode
            value={pass.guestCode}
            size={200}
            aria-label={`QR code for guest ${pass.guestName}`}
          />
        </div>
        <p className="text-slate-400 text-xs mt-4">Show this code at the gate</p>
        <p className="font-mono text-sm text-slate-700 mt-1 break-all">
          {pass.guestCode}
        </p>
      </div>
    </div>
  );
}
